import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade3Zhuyin.css";

/** Shuffle array for random quiz questions */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade3Zhuyin
 *  - Focus on daily conversation in Zhuyin, more fluid sentences & Q/A
 *  - 25-question quiz about more complex phrases, real dialogues
 */

const conversationNotes = (
  <div className="g3z-convo-notes">
    <h4>Zhuyin for Daily Conversations</h4>
    <p>
      By Grade 3, you can start practicing small dialogues fully in Zhuyin.  
      Example: ㄨㄛˇ ㄔ ㄅㄨˋ ㄒㄧ ㄏㄨㄢˇ ㄌㄚˋ ㄐㄧㄠˋ? (Do I dislike spicy peppers?)
    </p>
    <ul>
      <li>Ask about days: ㄐㄧㄣ ㄊㄧㄢ ㄒㄧㄥ ㄑㄧˊ ㄐㄧˇ? (What day is today?)</li>
      <li>Comment on preferences: ㄨㄛˇ ㄗㄞˋ ㄒㄧ ㄨㄢˊ (I’m playing games)</li>
      <li>Build your reading speed: read short paragraphs in Zhuyin to reinforce character recognition.</li>
    </ul>
  </div>
);

// Single Zhuyin-based pool (25 Q) for Grade 3
const zhuyinQuestions = [
  {
    question: "How to say 'What’s the matter?' => 你怎么了？ in Zhuyin?",
    correctAnswer: "ㄋㄧˇ ㄗㄣˇ ㄇㄜ˙ ㄌㄜ˙",
    options: [
      "ㄋㄧˊ ㄇㄟˊ ㄧㄡˇ ㄌㄜ˙",
      "ㄋㄧˇ ㄗㄣˇ ㄇㄜ˙ ㄌㄜ˙",
      "ㄋㄧˇ ㄨㄛˇ ㄇㄜ˙"
    ]
  },
  {
    question: "Which phrase might represent 'I’m playing a game' => '我在玩游戏'?",
    correctAnswer: "ㄨㄛˇ ㄗㄞˋ ㄨㄢˊ ㄧㄡˊ ㄒㄧˋ",
    options: [
      "ㄨㄛˇ ㄓㄜˋ ㄍㄜ˙ ㄧㄡˇ ㄒㄧˋ",
      "ㄨㄛˇ ㄗㄞˋ ㄨㄢˊ ㄧㄡˊ ㄒㄧˋ",
      "ㄨㄛˇ ㄨㄢˇ ㄗㄞˋ ㄒㄧˋ"
    ]
  },
  {
    question: "If you see ㄒㄧㄥ ㄑㄧˊ ㄐㄧˇ => 'xīngqī jǐ' means?",
    correctAnswer: "Which day (of the week) is it?",
    options: [
      "What time is it?",
      "How is the weather?",
      "Which day is it?"
    ]
  },
  {
    question: "A short question: ㄨㄛˇ ㄍㄢˇ ㄐㄩㄝˊ ㄅㄨˋ ㄕㄨ ㄈㄨ => means 'I feel uncomfortable.' The tone on ㄕㄨ?",
    correctAnswer: "shu1 (1st tone), but 'uncomfortable' uses 2nd char bù shūfú => 'unwell'",
    options: [
      "Always 4th tone for ㄕㄨ",
      "It’s 1st tone (shu) as in 'comfortable'",
      "No tone needed"
    ]
  },
  {
    question: "‘I like reading in my spare time’ => ㄨㄛˇ ㄒㄧˇ ㄏㄨㄢ ㄎㄢˋ ㄕㄨ => means?",
    correctAnswer: "Wǒ xǐhuān kàn shū: 'I like reading books'",
    options: [
      "I enjoy playing sports",
      "I like cooking noodles",
      "I like reading books"
    ]
  },
  {
    question: "When you see ㄑㄧㄥˇ ㄨㄣˋ, ㄕˋ ㄐㄧㄢ ㄒㄧㄢ ㄗㄞˋ ㄐㄧㄢ? => 'Excuse me, what time is it now?' might be spelled?",
    correctAnswer: "ㄑㄧㄥˇ ㄨㄣˋ ㄒㄧㄢˋ ㄗㄞˋ ㄐㄧ ㄌㄧㄠˇ (time?), variations exist but tone context needed",
    options: [
      "No standard approach, we guess from context",
      "As is: partial question asking time",
      "We never ask time in Zhuyin"
    ]
  },
  {
    question: "To say 'I’m hungry, let’s eat!' => ㄨㄛˇ ㄜˋ ㄌㄜ˙, ㄨㄛˇ ㄇㄣ ㄔ ㄅㄚ˙ => means?",
    correctAnswer: "'I’m hungry, let’s eat!'",
    options: [
      "I’m not hungry now",
      "I’m tired, let’s sleep",
      "I’m hungry, let’s eat!"
    ]
  },
  {
    question: "Zhuyin for '家人都很好' => ㄐㄧㄚ ㄖㄣˊ ㄉㄡ ㄏㄣˇ ㄏㄠˇ => means?",
    correctAnswer: "'All my family members are good/well.'",
    options: [
      "I have no family",
      "All my family is poor",
      "All my family is well"
    ]
  },
  {
    question: "To ask 'When will you come back?' => 你什么时候回来？ => ㄋㄧˇ ㄕㄣˊ ㄇㄜ˙ ㄕˊ ㄏㄡˋ ㄏㄨㄟˊ ㄌㄞˊ?",
    correctAnswer: "‘nǐ shénme shíhòu huílái?’ spelled in Zhuyin with tone marks as well",
    options: [
      "No direct way to express time in Zhuyin",
      "Yes, use ㄕˊ ㄏㄡˋ, ㄏㄨㄟˊ ㄌㄞˊ etc. with tones",
      "We always use pinyin for that question"
    ]
  },
  {
    question: "ㄋㄧˇ ㄏㄨㄟˋ ㄕㄨㄛ ㄧㄥ ㄨㄣˊ ㄇㄚ˙ => 'Can you speak English?' We see 3rd tone on ㄏㄨㄟˋ, meaning?",
    correctAnswer: "huì (3rd tone) = ‘can, be able to’",
    options: [
      "hui (no meaning)",
      "Always 1st tone",
      "3rd tone => ‘can, be able to’"
    ]
  },
  {
    question: "Which phrase might read: ㄨㄛˇ ㄇㄟˊ ㄧㄡˇ ㄓㄨ ㄧㄣ ㄦˊ => 'I don’t have Zhuyin er...'? Possibly incomplete but suggests?",
    correctAnswer: "I don’t have/know Bopomofo? Context needed, possibly incomplete",
    options: [
      "A full sentence about reading books",
      "No meaning at all in Chinese",
      "Might be incomplete, but means 'I don’t have Zhuyin' or 'I lack Bopomofo knowledge'"
    ]
  },
  {
    question: "Asking politely: ㄧㄣ ㄨㄟˋ ㄨㄛˇ ㄧㄡˇ ㄍㄨㄥ ㄎㄜˋ => Because I have homework, so?",
    correctAnswer: "后面 might be ㄙㄨㄛˇ ㄧˇ ㄅㄨˋ ㄋㄥˊ ㄔㄨ ㄑㄩ => I can’t go out. Typical daily usage",
    options: [
      "It means 'I love to do homework always'",
      "No further statement needed",
      "Likely leads to 'so I can’t go out' or similar"
    ]
  },
  {
    question: "A phrase: ㄓㄜˋ ㄍㄜ˙ ㄕˋ ㄕㄚ ㄇㄜ˙? => 'zhè ge shì shénme?' means?",
    correctAnswer: "'What is this?'",
    options: [
      "'Where is that?'",
      "'When is that?'",
      "'What is this?'"
    ]
  },
  {
    question: "ㄨㄟˊ ㄕㄣˊ ㄇㄜ˙ => 'wèi shénme' means 'Why?' If you see ㄋㄧˇ ㄨㄟˊ ㄕㄣˊ ㄇㄜ˙ ㄅㄨˋ ㄒㄧ ㄏㄨㄢˇ... => 'Why don't you like...?'",
    correctAnswer: "Yes, it’s a typical question in daily conversation",
    options: [
      "No, it means 'Where are you from?'",
      "Yes, that’s a typical 'Why don't you like...?' question",
      "It’s always used for 'What time is it?'"
    ]
  },
  {
    question: "Asking 'Do you have free time this afternoon?' => ㄒㄧㄚˋ ㄨˇ ㄋㄧˇ ㄧㄡˇ ㄎㄨㄥˋ ㄇㄚ˙ => means?",
    correctAnswer: "xiàwǔ nǐ yǒu kòng ma? => 'Are you free this afternoon?'",
    options: [
      "It means 'You must rest now?'",
      "Yes, 'Are you free this afternoon?'",
      "Asking for directions to a park"
    ]
  },
  {
    question: "To express '我不会写汉字' => ㄨㄛˇ ㄅㄨˋ ㄏㄨㄟˋ ㄒㄧㄝˇ ㄏㄢˋ ㄗˋ => means?",
    correctAnswer: "'I can’t write Chinese characters.'",
    options: [
      "'I don’t like to speak Chinese.'",
      "'I can’t write Chinese characters.'",
      "I can write them easily"
    ]
  },
  {
    question: "Combining 3 or 4 characters in Zhuyin requires careful tone placement. Example: ㄨㄛˇ ㄒㄧㄤˇ ㄑㄧㄥˇ ㄋㄧˇ => 'I want to invite you...' This is important because?",
    correctAnswer: "Multi-character sentences reflect actual daily usage with correct tone flow",
    options: [
      "We only read single characters in real life",
      "Zhuyin can’t handle multi-syllable words",
      "Daily usage needs correct tone flow across multiple characters"
    ]
  },
  {
    question: "If you see ㄌㄧˇ ㄅㄞˋ ㄦˋ => lǐbài èr, it might mean 'Tuesday' in some regions. Another way is xīngqī èr => both show?",
    correctAnswer: "Zhuyin can represent different synonyms for days (礼拜 vs. 星期)",
    options: [
      "We only use xīngqī, not lǐbài",
      "Days can’t be spelled in Zhuyin",
      "Zhuyin can represent different synonyms (礼拜, 星期)"
    ]
  },
  {
    question: "A daily phrase: ㄨㄛˇ ㄒㄧ ㄏㄨㄢ ㄏㄠˋ ㄏㄜ ㄉㄨㄛ ㄕㄨㄟˇ => 'I like to drink a lot of water.' Tones matter for?",
    correctAnswer: "ㄒㄧˇ, ㄏㄨㄢˇ, ㄏㄠˋ differ from ㄒㄧ, ㄏㄨㄢ, ㄏㄠ if tones change meaning",
    options: [
      "No difference in meaning",
      "We ignore tone on water words",
      "Yes, each must have correct tone to maintain meaning"
    ]
  },
  {
    question: "Pronouncing ㄓㄨㄢˇ ㄈㄤˇ => 'zhuǎnfǎng' or 'zhuǎn fǎng' can mean 'transfer visit' or 'to shift a visit' in advanced usage. This shows?",
    correctAnswer: "Zhuyin can handle even complex or formal words with correct tone marks",
    options: [
      "We cannot handle formal words in Zhuyin",
      "Yes, with correct tone, it expresses advanced vocabulary",
      "We must revert to pinyin for advanced words"
    ]
  },
  {
    question: "Lastly, building your reading speed in Zhuyin helps you?",
    correctAnswer: "Transition smoothly to reading full characters with correct pronunciation",
    options: [
      "Forget about characters entirely",
      "Only read kids’ books forever",
      "Transition to full character reading with correct pronunciation"
    ]
  }
];

export default function Grade3Zhuyin() {
  // Single topic => Zhuyin
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 mins

  // Timer
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  // Quick Quiz (random Q from zhuyinQuestions)
  const generateQuiz = () => {
    const q = zhuyinQuestions[Math.floor(Math.random() * zhuyinQuestions.length)];
    setQuiz({
      question: q.question,
      correctAnswer: q.correctAnswer,
      options: shuffleArray([...q.options])
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    setTimeout(() => {
      generateQuiz();
    }, 1500);
  };

  // Final Quiz => entire zhuyinQuestions
  const startFinalQuiz = () => {
    const finalSet = shuffleArray(zhuyinQuestions);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex].correctAnswer === choice) {
      setFinalQuizScore(prev => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex(prev => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g3chi-page">
      <h1 className="g3chi-title">Grade 3 Zhuyin: Daily Conversations</h1>
      <p className="g3chi-description">
        Practice daily conversation phrases & short dialogues in Zhuyin!
      </p>

      {/* Lesson Content */}
      <div className="g3chi-lesson-content">
        <h2 className="g3chi-lesson-title">注音: More Fluent Dialogues</h2>
        <p className="g3chi-lesson-description">
          At Grade 3, you can handle multi-character sentences, ask questions, and expand your vocabulary for daily chats.
        </p>
        <p className="g3chi-lesson-example">
          <strong>Example:</strong> ㄋㄧˇ ㄗㄣˇ ㄇㄜ˙ ㄌㄜ˙? (nǐ zěnme le?) “What’s wrong?”
        </p>
        {conversationNotes}
      </div>

      {/* Quiz Section */}
      <div className="g3chi-quiz-double-container">
        {/* Quick Quiz */}
        <div className="g3chi-quick-quiz-container">
          <h2 className="g3chi-quiz-title">Quick Quiz!</h2>
          {!quiz && (
            <button className="g3chi-quiz-btn" onClick={generateQuiz}>
              Start Quick Quiz
            </button>
          )}
          {quiz && (
            <>
              <p className="g3chi-quiz-question">{quiz.question}</p>
              <div className="g3chi-quiz-options">
                {quiz.options.map((opt, i) => (
                  <button
                    key={i}
                    className={`g3chi-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                    onClick={() => handleAnswerClick(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {feedback && <p className="g3chi-quiz-feedback">{feedback}</p>}
            </>
          )}
        </div>

        {/* Final Quiz */}
        <div className="g3chi-final-quiz-container">
          <h2 className="g3chi-quiz-title">Final Quiz</h2>
          {!finalQuizActive && (
            <button className="g3chi-final-quiz-button" onClick={startFinalQuiz}>
              Start Final Quiz (25 Questions)
            </button>
          )}
          {finalQuizActive && finalQuizQuestions.length > 0 && (
            <div className="g3chi-final-quiz-content">
              <p>
                Time Left: {Math.floor(timeLeft / 60)}:
                {String(timeLeft % 60).padStart(2, "0")}
              </p>
              <p>
                Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
              </p>
              <p className="g3chi-quiz-question">
                {finalQuizQuestions[finalQuizIndex].question}
              </p>
              <div className="g3chi-quiz-options">
                {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                  <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                    {choice}
                  </button>
                ))}
              </div>
              <p>Score: {finalQuizScore}</p>
              {finalQuizIndex === finalQuizQuestions.length - 1 && (
                <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Link to="/ChineseSubject" className="g3chi-back-button">
        ← Back to Chinese Page
      </Link>
    </div>
  );
}
